import { useEffect } from "react";
import { NavLink, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { InlineLogo } from "../../components/brand/icons";

const SECTIONS = [
  { to: "/admin", label: "Dashboard", end: true },
  { to: "/admin/home", label: "Home" },
  { to: "/admin/editions", label: "Latest Edition" },
  { to: "/admin/exhibitions", label: "Exhibitions" },
  { to: "/admin/recent-shows", label: "Recent Shows" },
  { to: "/admin/cv", label: "CV" },
  { to: "/admin/press", label: "Press" },
  { to: "/admin/enquiries", label: "Enquiries" },
  { to: "/admin/settings", label: "Site Settings" },
  { to: "/admin/users", label: "Users", admin: true },
];

export const AdminLayout = () => {
  const { user, logout } = useAuth();

  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
    document.title = "Pantheon CMS";
    return () => el.setAttribute("content", "index, follow");
  }, []);

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-offwhite" data-testid="admin-loading">
        <p className="eyebrow">Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const sections = SECTIONS.filter((s) => !s.admin || user.role === "admin");

  return (
    <div className="min-h-screen bg-offwhite text-charcoal" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-56 flex-col border-r border-charcoal lg:flex">
        <div className="border-b border-charcoal px-6 py-5">
          <InlineLogo />
        </div>
        <nav className="flex-1 overflow-y-auto px-6 py-6" aria-label="CMS">
          <ul className="space-y-4">
            {sections.map((s) => (
              <li key={s.to}>
                <NavLink
                  to={s.to}
                  end={s.end}
                  data-testid={`admin-nav-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-condensed text-xs font-semibold uppercase tracking-[0.14em] transition-opacity duration-200 ${
                      isActive ? "opacity-100 underline underline-offset-4" : "opacity-50 hover:opacity-100"
                    }`
                  }
                >
                  {s.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="border-t border-charcoal px-6 py-4">
          <p className="truncate text-xs font-light" data-testid="admin-user-email">{user.email}</p>
          <button type="button" onClick={logout} data-testid="admin-sign-out" className="link-arrow mt-2 text-xs">
            Sign out
          </button>
        </div>
      </aside>

      <div className="lg:hidden">
        <div className="flex items-center justify-between border-b border-charcoal px-5 py-4">
          <InlineLogo />
          <button type="button" onClick={logout} data-testid="admin-sign-out-mobile" className="link-arrow text-xs">
            Sign out
          </button>
        </div>
        <nav className="flex gap-5 overflow-x-auto border-b border-charcoal px-5 py-3" aria-label="CMS">
          {sections.map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              end={s.end}
              className={({ isActive }) =>
                `whitespace-nowrap font-condensed text-xs font-semibold uppercase tracking-[0.14em] ${isActive ? "opacity-100 underline underline-offset-4" : "opacity-50"}`
              }
            >
              {s.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="px-5 py-8 lg:ml-56 lg:px-12 lg:py-12">
        <Outlet />
      </main>
    </div>
  );
};
